<template>
    <div class="time-stats">
        <div class="time-stats-header">
            <h2 class="time-stats-title">{{ $t('titles_JournalStats.time') }}</h2>
            <div class="time-stats-tabs" role="tablist">
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    type="button"
                    role="tab"
                    class="time-stats-tab"
                    :class="{ 'time-stats-tab--active': activeTab === tab.id }"
                    :aria-selected="activeTab === tab.id"
                    @click="activeTab = tab.id"
                >
                    <v-icon small class="mr-2">fa-regular {{ tab.icon }}</v-icon>
                    {{ $t(tab.labelKey) }}
                </button>
            </div>
        </div>

        <div v-show="activeTab === 'bestDaysHours'" class="time-stats-panel">
            <h3 class="heatmap-title">{{ $t('timeStats.bestDaysHours.title') }}</h3>
            <p class="heatmap-subtitle">{{ $t('timeStats.bestDaysHours.subtitle') }}</p>

            <div class="heatmap-wrapper">
                <div class="heatmap-grid">
                    <div
                        v-for="day in weekDays"
                        :key="day.index"
                        class="heatmap-row"
                    >
                        <div class="heatmap-day-label">{{ day.label }}</div>
                        <div
                            v-for="hour in hours"
                            :key="day.index + '-' + hour"
                            class="heatmap-cell"
                            :class="getCellClass(day.index, hour)"
                        >
                            {{ formatCellValue(day.index, hour) }}
                        </div>
                        <div class="heatmap-cell heatmap-cell--sum" :class="getSumClass(day.index)">
                            {{ formatSumValue(day.index) }}
                        </div>
                    </div>

                    <div class="heatmap-row heatmap-row--footer">
                        <div class="heatmap-corner"></div>
                        <div
                            v-for="hour in hours"
                            :key="'h-' + hour"
                            class="heatmap-axis-label"
                        >
                            {{ formatHourLabel(hour) }}
                        </div>
                        <div class="heatmap-axis-label heatmap-sum-header">{{ $t('timeStats.sum') }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div v-show="activeTab === 'holdingTime'" class="time-stats-panel">
            <h3 class="heatmap-title">{{ $t('timeStats.holdingTime.title') }}</h3>
            <p class="heatmap-subtitle">{{ $t('timeStats.holdingTime.subtitle') }}</p>
            <div class="holding-chart">
                <apexchart
                    v-if="activeTab === 'holdingTime'"
                    type="scatter"
                    height="400"
                    :options="holdingTimeOptions"
                    :series="holdingTimeSeries"
                />
            </div>
        </div>

        <JournalInsightBar :insight="insight" />
    </div>
</template>

<script>
import JournalInsightBar from '@/views/Journal/components/JournalInsightBar';

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const CHART_GREEN = '#005931';
const CHART_RED = '#D00808';

const HOLDING_BUCKETS = [
    { key: '0_10', min: 0, max: 10 },
    { key: '10_60', min: 10, max: 60 },
    { key: '1_12h', min: 60, max: 720 },
    { key: '12_24h', min: 720, max: 1440 },
    { key: '1plus', min: 1440, max: Infinity },
];

function createEmptyScatterOptions() {
    return {
        chart: {
            type: 'scatter',
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        legend: { show: false },
        markers: { size: 5, strokeWidth: 0 },
        grid: {
            borderColor: '#f3f4f6',
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
        },
        xaxis: {
            min: -0.5,
            max: 4.5,
            tickAmount: 5,
            labels: { show: true },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            min: -10,
            max: 10,
            tickAmount: 4,
            labels: { show: true },
        },
    };
}

export default {
    name: "TimeStats",
    components: { JournalInsightBar },
    props: {
        // AI insight for this section, already resolved by JournalStats (null = nothing to show).
        insight: { type: Object, default: null },
    },
    data() {
        return {
            activeTab: 'bestDaysHours',
            tabs: [
                { id: 'bestDaysHours', labelKey: 'timeStats.tabs.bestDaysHours', icon: 'fa-calendar' },
                { id: 'holdingTime', labelKey: 'timeStats.tabs.holdingTime', icon: 'fa-hourglass' },
            ],
            hours: Array.from({ length: 24 }, (_, i) => i),
            heatmapGrid: {},
            holdingTimeSeries: [{ name: 'Winners', data: [] }, { name: 'Losers', data: [] }],
            holdingTimeOptions: createEmptyScatterOptions(),
        };
    },
    computed: {
        weekDays() {
            return DAY_ORDER.map((index) => ({
                index,
                label: this.$t(`timeStats.days.${this.dayKey(index)}`),
            }));
        },
    },
    beforeMount() {
        this.refreshStats();
    },
    watch: {
        '$store.state.book.trades': function () {
            this.refreshStats();
        },
        '$i18n.locale': function () {
            this.buildHoldingTimeChart();
        },
    },
    methods: {
        refreshStats() {
            this.buildHeatmap();
            this.buildHoldingTimeChart();
        },
        dayKey(dayIndex) {
            const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            return keys[dayIndex];
        },
        formatHourLabel(hour) {
            return String(hour).padStart(2, '0');
        },
        buildHeatmap() {
            const grid = {};
            DAY_ORDER.forEach((dayIndex) => {
                grid[dayIndex] = Array(24).fill(0);
            });

            this.$store.state.book.trades.forEach((trade) => {
                const day = trade.openTime.getDay();
                const hour = trade.openTime.getHours();
                if (!grid[day]) {
                    grid[day] = Array(24).fill(0);
                }
                if (trade.netProfit > 0) {
                    grid[day][hour] += 1;
                } else if (trade.netProfit < 0) {
                    grid[day][hour] -= 1;
                }
            });

            this.heatmapGrid = grid;
        },
        getCellValue(dayIndex, hour) {
            return (this.heatmapGrid[dayIndex] && this.heatmapGrid[dayIndex][hour]) || 0;
        },
        getDaySum(dayIndex) {
            const row = this.heatmapGrid[dayIndex];
            if (!row) {
                return 0;
            }
            return row.reduce((total, value) => total + value, 0);
        },
        formatCellValue(dayIndex, hour) {
            const value = this.getCellValue(dayIndex, hour);
            if (value === 0) {
                return '';
            }
            return value > 0 ? `+${value}` : String(value);
        },
        formatSumValue(dayIndex) {
            const value = this.getDaySum(dayIndex);
            if (value === 0) {
                return '0';
            }
            return value > 0 ? `+${value}` : String(value);
        },
        getCellClass(dayIndex, hour) {
            const value = this.getCellValue(dayIndex, hour);
            if (value === 0) {
                return 'heatmap-cell--empty';
            }
            const abs = Math.abs(value);
            if (value > 0) {
                return abs >= 12 ? 'heatmap-cell--pos-high' : 'heatmap-cell--pos-low';
            }
            return abs >= 12 ? 'heatmap-cell--neg-high' : 'heatmap-cell--neg-low';
        },
        getSumClass(dayIndex) {
            const value = this.getDaySum(dayIndex);
            if (value === 0) {
                return 'heatmap-cell--sum-neutral';
            }
            return value > 0 ? 'heatmap-cell--sum-positive' : 'heatmap-cell--sum-negative';
        },
        getMinutesFromDuration(duration) {
            if (!duration) {
                return 0;
            }
            if (typeof duration === 'string') {
                const iso8601DurationRegex = /(-)?P(?:([.,\d]+)Y)?(?:([.,\d]+)M)?(?:([.,\d]+)W)?(?:([.,\d]+)D)?T(?:([.,\d]+)H)?(?:([.,\d]+)M)?(?:([.,\d]+)S)?/;
                const matches = duration.match(iso8601DurationRegex);
                if (!matches) {
                    return 0;
                }
                duration = {
                    years: matches[2] === undefined ? 0 : matches[2],
                    months: matches[3] === undefined ? 0 : matches[3],
                    weeks: matches[4] === undefined ? 0 : matches[4],
                    days: matches[5] === undefined ? 0 : matches[5],
                    hours: matches[6] === undefined ? 0 : matches[6],
                    minutes: matches[7] === undefined ? 0 : matches[7],
                    seconds: matches[8] === undefined ? 0 : matches[8],
                };
            }
            let minutes = (duration.seconds * 1) / 60;
            minutes += duration.minutes * 1;
            minutes += duration.hours * 1 * 60;
            minutes += duration.days * 1 * 1440;
            minutes += duration.weeks * 1 * 10080;
            minutes += duration.months * 1 * 40320;
            minutes += duration.years * 1 * 483840;
            return minutes;
        },
        getTradeDurationMinutes(trade) {
            if (trade.tradeTime) {
                return this.getMinutesFromDuration(trade.tradeTime);
            }
            if (trade.openTime && trade.closeTime) {
                return (trade.closeTime.getTime() - trade.openTime.getTime()) / 60000;
            }
            return 0;
        },
        getHoldingBucketIndex(minutes) {
            for (let i = 0; i < HOLDING_BUCKETS.length; i += 1) {
                const bucket = HOLDING_BUCKETS[i];
                if (minutes >= bucket.min && minutes < bucket.max) {
                    return i;
                }
            }
            return HOLDING_BUCKETS.length - 1;
        },
        getBucketJitter(seed) {
            const normalized = Math.abs(Number(seed) || 0) % 1000;
            return (normalized / 1000) * 0.7 - 0.35;
        },
        buildHoldingTimeChart() {
            const winners = [];
            const losers = [];
            let maxAbsProfit = 0;

            this.$store.state.book.trades.forEach((trade, index) => {
                const durationMinutes = this.getTradeDurationMinutes(trade);
                const bucketIndex = this.getHoldingBucketIndex(durationMinutes);
                const x = bucketIndex + this.getBucketJitter(trade.id || index);
                const y = trade.netProfit;
                maxAbsProfit = Math.max(maxAbsProfit, Math.abs(y));

                if (y >= 0) {
                    winners.push([x, y]);
                } else {
                    losers.push([x, y]);
                }
            });

            const yLimit = Math.max(maxAbsProfit * 1.15, 10);
            const bucketLabels = HOLDING_BUCKETS.map((bucket) =>
                this.$t(`timeStats.holdingTime.buckets.${bucket.key}`)
            );
            const dividerAnnotations = [0.5, 1.5, 2.5, 3.5].map((x) => ({
                x,
                borderColor: '#e5e7eb',
                strokeDashArray: 0,
            }));

            this.holdingTimeSeries = [
                { name: this.$t('timeStats.holdingTime.winners'), data: winners },
                { name: this.$t('timeStats.holdingTime.losers'), data: losers },
            ];

            this.holdingTimeOptions = {
                theme: {
                    mode: this.$store.state.currentTheme == 'dark' ? 'dark' : 'light',
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    },
                },
                colors: [CHART_GREEN, CHART_RED],
                chart: {
                    type: 'scatter',
                    toolbar: { show: false },
                    zoom: { enabled: false },
                },
                markers: {
                    size: 5,
                    strokeWidth: 0,
                },
                legend: {
                    show: false,
                },
                grid: {
                    borderColor: '#f3f4f6',
                    strokeDashArray: 4,
                    xaxis: { lines: { show: false } },
                    yaxis: { lines: { show: true } },
                },
                annotations: {
                    xaxis: dividerAnnotations,
                    yaxis: [{
                        y: 0,
                        borderColor: '#d1d5db',
                        strokeDashArray: 0,
                    }],
                },
                xaxis: {
                    min: -0.5,
                    max: 4.5,
                    tickAmount: 5,
                    labels: {
                        formatter: (value) => {
                            const index = Math.round(value);
                            return bucketLabels[index] || '';
                        },
                        style: {
                            colors: '#ababab',
                            fontSize: '12px',
                            fontFamily: 'Avenir, sans-serif',
                        },
                    },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                    tooltip: { enabled: false },
                },
                yaxis: {
                    min: -yLimit,
                    max: yLimit,
                    tickAmount: 4,
                    labels: {
                        formatter: (value) => {
                            if (Math.abs(value) < 0.01) {
                                return '$0';
                            }
                            const numberValue = Math.abs(value) >= 1000
                                ? `${(Math.abs(value) / 1000).toFixed(1)}k`
                                : Math.abs(value).toFixed(0);
                            return value >= 0 ? `$${numberValue}` : `-$${numberValue}`;
                        },
                        style: {
                            colors: '#ababab',
                            fontSize: '12px',
                            fontFamily: 'Avenir, sans-serif',
                        },
                    },
                },
                tooltip: {
                    x: {
                        formatter: (val) => {
                            const index = Math.min(
                                HOLDING_BUCKETS.length - 1,
                                Math.max(0, Math.round(val))
                            );
                            return bucketLabels[index];
                        },
                    },
                    y: {
                        formatter: (val) => `$${val.toFixed(2)}`,
                    },
                },
            };
        },    },
};
</script>

<style lang="scss" scoped>
.time-stats {
    .time-stats-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
        margin-bottom: 24px;
    }

    .time-stats-title {
        margin: 0;
        font-family: 'Avenir', sans-serif;
        font-weight: 700;
        font-size: 28px;
        color: var(--v-darkGrey-base);
    }

    .time-stats-tabs {
        display: inline-flex;
        align-items: center;
        background: var(--v-lightGray-base);
        border-radius: 8px;
        padding: 4px;
    }

    .time-stats-tab {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 8px 20px;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: var(--v-darkGray-base);
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s, color 0.2s;
        white-space: nowrap;

        .v-icon {
            color: inherit !important;
        }

        &--active {
            background: var(--v-mainGreen-base);
            color: #F8F8F8;
        }
    }

    .time-stats-panel {
        margin-top: 8px;
    }

    .heatmap-title {
        margin: 0 0 4px;
        font-family: 'Avenir', sans-serif;
        font-weight: 700;
        font-size: 16px;
        color: var(--v-mainBlack-base);
    }

    .heatmap-subtitle {
        margin: 0 0 20px;
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        line-height: 1.4;
        color: #ababab;
    }

    .holding-chart {
        margin-top: 4px;
    }

    .heatmap-wrapper {
        overflow-x: auto;
    }

    .heatmap-grid {
        display: table;
        border-collapse: separate;
        border-spacing: 4px;
        min-width: 100%;
    }

    .heatmap-row {
        display: table-row;
    }

    .heatmap-row--footer {
        .heatmap-axis-label {
            padding-top: 8px;
            vertical-align: top;
        }

        .heatmap-corner {
            vertical-align: top;
        }
    }

    .heatmap-corner,
    .heatmap-day-label,
    .heatmap-axis-label,
    .heatmap-cell {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }

    .heatmap-corner {
        width: 88px;
        min-width: 88px;
    }

    .heatmap-day-label {
        width: 88px;
        min-width: 88px;
        padding-right: 12px;
        text-align: left;
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        font-weight: 500;
        color: #ababab;
        white-space: nowrap;
    }

    .heatmap-axis-label {
        min-width: 36px;
        height: 28px;
        font-family: 'Avenir', sans-serif;
        font-size: 13px;
        font-weight: 500;
        color: #ababab;
    }

    .heatmap-sum-header {
        min-width: 52px;
        font-weight: 600;
    }

    .heatmap-cell {
        min-width: 36px;
        width: 36px;
        height: 36px;
        border-radius: 4px;
        font-family: 'Avenir', sans-serif;
        font-size: 13px;
        font-weight: 600;
        color: #fff;

        &--empty {
            background: #f3f4f6;
            color: transparent;
        }

        &--pos-low {
            background: #7EDFB3;
        }

        &--pos-high {
            background: #005931;
        }

        &--neg-low {
            background: #FFA2A2;
        }

        &--neg-high {
            background: #D00808;
        }

        &--sum {
            min-width: 52px;
            width: 52px;
            font-weight: 700;
        }

        &--sum-neutral {
            background: #f3f4f6;
            color: #ababab;
        }

        &--sum-positive {
            background: #005931;
            color: #fff;
        }

        &--sum-negative {
            background: #D00808;
            color: #fff;
        }
    }

}
</style>
